import { Alert, Button, Col, Form, Input, Modal, Radio, Row, Select, Switch } from "antd";
import { CheckCircleFilled, MobileOutlined, WalletOutlined } from "@ant-design/icons";
import { useContext } from "react";
import ContactNumberFormItem from "../components/ContactNumberFormItem";
import { sessionStore, SESSION_ATTRS } from "../store/session";
import { getDialCode } from "../utils/countryCodes";
import { ACCESS_CREDENTIAL_MODE, ACCESS_CREDENTIAL_PROVIDER, DEFAULT_ACCESS_CREDENTIAL_POLICY, HID_WALLET_PASS_STATUS, USER_MODAL_TYPE } from "./constants";
import { UserServiceFormSections } from "./serviceIntegrations";
import WalletCredentialList from "./WalletCredentialList";
import { WalletCredential, isTerminalWalletCredential } from "./walletCredentials";
import { isTenantVisitorPassDelegationEnabled, isTenantWalletEnabled } from "../utils/tenantFeatures";

type PropType = {
    onClose: Function,
    onSubmit: (data:any) => Promise<any>,
    mode: string,
    editRowData: any,
    isLoading?: boolean,
}

const FormItem = Form.Item;

const NAME_ITEMS_LAYOUT = {
    labelCol: { span: 23 },
    wrapperCol: { span: 23 }
}

const NewUserModal = ({ onClose, onSubmit, mode, editRowData, isLoading = false }: PropType) => {

    const [form] = Form.useForm();

    const {
        firstName,
        lastName,
        email,
        contactNumber,
        licensePlate,
        buildingId,
        tenantId,
        countryCode,
        accessCredentialPolicy,
        visitorPassDelegationEnabled,
        inceptionPermissionGroupIds
    } = editRowData;

    const { state } = useContext(sessionStore);
    const buildings: any = state.get(SESSION_ATTRS.BUILDINGS);
    const tenants: any = state.get(SESSION_ATTRS.TENANTS);
    const selectedTenantId = (Form.useWatch("tenantId", form) as string | undefined) || tenantId;
    const selectedCredentialMode = (Form.useWatch("accessCredentialMode", form) as string | undefined)
        || accessCredentialPolicy?.mode
        || DEFAULT_ACCESS_CREDENTIAL_POLICY.mode;
    const selectedTenant = tenants.find((tenant: any) => tenant.tenantId === selectedTenantId);
    const walletEnabled = isTenantWalletEnabled(selectedTenant);
    const tenantVisitorPassDelegationEnabled = isTenantVisitorPassDelegationEnabled(selectedTenant);
    const tenantBuilding = buildings.filter((building: any) => building.buildingId === selectedTenant?.buildingId);

    const onSubmitClick = async () => {
        if (isLoading) {
            return;
        }

        try {
            let values = await form.validateFields();
            values.email = values.email.toLowerCase();
            values.countryDialingCode = getDialCode(values.countryCode);
            if (mode === USER_MODAL_TYPE.CREATE && !walletEnabled) {
                values.accessCredentialMode = ACCESS_CREDENTIAL_MODE.MOBILE_APP;
            }
            if (!tenantVisitorPassDelegationEnabled) {
                values.visitorPassDelegationEnabled = false;
            }
            values.accessCredentialPolicy = {
                provider: ACCESS_CREDENTIAL_PROVIDER.HID,
                mode: values.accessCredentialMode
            };
            delete values.accessCredentialMode;
            onSubmit(values);
        } catch (e) {
            console.error(e);
        }
    }

    const tenantOptions = (
        tenants.map((t: any) => <Select.Option key={t.tenantId} value={t.tenantId}>{t.name}</Select.Option>)
    );

    const onTenantChange = (nextTenantId: string) => {
        const nextTenant = tenants.find((tenant: any) => tenant.tenantId === nextTenantId);
        if (!isTenantWalletEnabled(nextTenant)) {
            form.setFieldsValue({ accessCredentialMode: ACCESS_CREDENTIAL_MODE.MOBILE_APP });
        }
        if (!isTenantVisitorPassDelegationEnabled(nextTenant)) {
            form.setFieldsValue({ visitorPassDelegationEnabled: false });
        }
    };

    const buildingOptions = (
        tenantBuilding.map(b => <Select.Option key={b.buildingId} value={b.buildingId}>{b.name}</Select.Option>)
    )

    const originalCredentialMode = accessCredentialPolicy?.mode || DEFAULT_ACCESS_CREDENTIAL_POLICY.mode;
    const isWalletCredential = originalCredentialMode === ACCESS_CREDENTIAL_MODE.WALLET;
    const walletCredentials: WalletCredential[] = Array.isArray(editRowData?.walletCredentials)
        ? editRowData.walletCredentials
        : [];
    const hasAccessCredential = isWalletCredential
        ? walletCredentials.length > 0
        : !!editRowData?.credentialId;
    const hasActiveCredential = isWalletCredential
        ? walletCredentials.some((credential) => credential.status === HID_WALLET_PASS_STATUS.ACTIVE)
        : editRowData?.credentialStatus === 'ACTIVE' && !!editRowData?.credentialId;
    const showCredentialTypeWarning = mode === USER_MODAL_TYPE.EDIT
        && selectedCredentialMode !== originalCredentialMode
        && hasActiveCredential;

    const renderAccessCardNumbers = () => {
        if (isWalletCredential && walletCredentials.length) {
            const currentCredentials = walletCredentials.filter((credential) => !isTerminalWalletCredential(credential));
            const credentialHistory = walletCredentials.filter(isTerminalWalletCredential);
            return (
                <div>
                    {currentCredentials.length > 0 && (
                        <>
                            <div style={{ fontWeight: 500, marginBottom: 6 }}>Current passes</div>
                            <WalletCredentialList credentials={currentCredentials} showTimestamps />
                        </>
                    )}
                    {credentialHistory.length > 0 && (
                        <div style={{ marginTop: currentCredentials.length ? 14 : 0 }}>
                            <div style={{ fontWeight: 500, marginBottom: 6 }}>Pass history</div>
                            <WalletCredentialList credentials={credentialHistory} showTimestamps />
                        </div>
                    )}
                </div>
            );
        }

        return <span>{editRowData.credentialId}</span>;
    }

    return (
        <Modal
            visible={true}
            footer={null}
            onCancel={() => onClose()}
            wrapClassName="add-new-user-modal"
            maskClosable={false}
            centered
            zIndex={1000}
        >
            <h2 className="new-user-title">{mode === USER_MODAL_TYPE.CREATE ? 'Add Single User' : 'Edit User'}</h2>
            <div className="add-user-form-wrp">
                <Form
                    layout="vertical"
                    form={form}
                    initialValues={{
                        firstName,
                        lastName,
                        email,
                        licensePlate,
                        tenantId,
                        buildingId,
                        contactNumber,
                        countryCode,
                        accessCredentialMode: accessCredentialPolicy?.mode || DEFAULT_ACCESS_CREDENTIAL_POLICY.mode,
                        visitorPassDelegationEnabled: visitorPassDelegationEnabled === true,
                        inceptionPermissionGroupIds: inceptionPermissionGroupIds || []
                    }}
                >
                    <Row>
                        <Col span={12}>
                            <FormItem
                                label="First Name"
                                name="firstName"
                                {...NAME_ITEMS_LAYOUT}
                                rules={[{ required: true, message: 'Please enter first name' }]}
                            >
                                <Input placeholder="First Name" />
                            </FormItem>
                        </Col>
                        <Col span={12}>
                            <FormItem
                                label="Last Name"
                                name="lastName"
                                rules={[{ required: true, message: 'Please enter last name' }]}
                            >
                                <Input placeholder="Last Name" />
                            </FormItem>
                        </Col>
                    </Row>
                    <ContactNumberFormItem placeHolder="Phone number" isRequired={true} />
                    <FormItem
                        label="Email"
                        name="email"
                        rules={[{ type: 'email', message: 'Please enter a valid email' },
                        { required: true, message: 'Please enter email' }]}
                    >
                        <Input disabled={mode === USER_MODAL_TYPE.EDIT} placeholder="Email" type="email" />
                    </FormItem>
                    <FormItem
                        label="License Plate"
                        name="licensePlate"
                    >
                        <Input placeholder="License plate" />
                    </FormItem>
                    <FormItem
                        label="Tenant"
                        name="tenantId"
                        rules={[{ required: true, message: 'Please select a tenant' }]}
                    >
                        <Select
                            disabled={mode === USER_MODAL_TYPE.EDIT}
                            placeholder="Pick a company"
                            onChange={onTenantChange}
                        >
                            {tenantOptions}
                        </Select>
                    </FormItem>
                    <FormItem
                        label="Credential Type"
                        name="accessCredentialMode"
                        rules={[{ required: true, message: 'Please select credential type' }]}
                        className="credential-type-form-item"
                        extra={showCredentialTypeWarning ? (
                            <Alert
                                type="warning"
                                showIcon
                                style={{ marginTop: 12 }}
                                message={`Saving this change will revoke the user's existing ${isWalletCredential ? 'Wallet passes' : 'Mobile credential'}. The user will need to activate the new credential type.`}
                            />
                        ) : undefined}
                    >
                        <Radio.Group className="credential-type-card-group">
                            <Radio value={ACCESS_CREDENTIAL_MODE.MOBILE_APP} className="credential-type-card">
                                <span className="credential-type-card__icon">
                                    <MobileOutlined />
                                </span>
                                <span className="credential-type-card__content">
                                    <span className="credential-type-card__title">Mobile credential</span>
                                    <span className="credential-type-card__description">
                                        Use the InTenant mobile app.
                                    </span>
                                </span>
                                {selectedCredentialMode === ACCESS_CREDENTIAL_MODE.MOBILE_APP && (
                                    <CheckCircleFilled className="credential-type-card__check" />
                                )}
                            </Radio>
                            {walletEnabled && (
                                <Radio value={ACCESS_CREDENTIAL_MODE.WALLET} className="credential-type-card">
                                    <span className="credential-type-card__icon">
                                        <WalletOutlined />
                                    </span>
                                    <span className="credential-type-card__content">
                                        <span className="credential-type-card__title">Wallet credential</span>
                                        <span className="credential-type-card__description">
                                            Issue an Apple or Google Wallet pass.
                                        </span>
                                    </span>
                                    {selectedCredentialMode === ACCESS_CREDENTIAL_MODE.WALLET && (
                                        <CheckCircleFilled className="credential-type-card__check" />
                                    )}
                                </Radio>
                            )}
                        </Radio.Group>
                    </FormItem>
                    {mode === USER_MODAL_TYPE.EDIT && hasAccessCredential && (
                        <FormItem label={isWalletCredential ? "Wallet Passes" : "Access Card Number"}>
                            <div style={{ minHeight: 32 }}>
                                {renderAccessCardNumbers()}
                            </div>
                        </FormItem>
                    )}
                    {tenantVisitorPassDelegationEnabled && (
                        <FormItem
                            className="visitor-delegation-form-item"
                        >
                            <div className="visitor-delegation-option">
                                <div className="visitor-delegation-option__content">
                                    <span className="visitor-delegation-option__title">Visitor pass delegation</span>
                                    <span className="visitor-delegation-option__description">
                                        Allow this user to issue temporary visitor Wallet passes.
                                    </span>
                                </div>
                                <FormItem name="visitorPassDelegationEnabled" valuePropName="checked" noStyle>
                                    <Switch checkedChildren="On" unCheckedChildren="Off" />
                                </FormItem>
                            </div>
                        </FormItem>
                    )}
                    <FormItem
                        label="Access"
                        name="buildingId"
                        rules={[{ required: true, message: 'Please select a building' }]}
                    >
                        <Select placeholder="Select a building">
                            {buildingOptions}
                        </Select>
                    </FormItem>
                    <UserServiceFormSections
                        form={form}
                        mode={mode}
                        editRowData={editRowData}
                        tenants={tenants}
                        buildings={buildings}
                        selectedTenantId={selectedTenantId}
                    />
                    <FormItem>
                        <Button
                            className="btn btn--primary new-user-manual-btn"
                            onClick={onSubmitClick}
                            loading={isLoading}
                            disabled={isLoading}
                        >
                            {mode === USER_MODAL_TYPE.CREATE ? 'Add New User' : 'Update User'}
                        </Button>
                    </FormItem>
                </Form>
            </div>
        </Modal>
    );
}

export default NewUserModal;
